// src/components/App.jsx
import React, { useState, useEffect } from 'react';
import Header from './Header';
import PaneLeft from './PaneLeft';
import PaneMain from './PaneMain';
import PaneRight from './PaneRight';
import { clearSession } from './AccountPanel';
import Footer from './Footer';
import '../styles/main.css';
import { API_BASE } from '../utils/apiBase';

function App() {
  const [savedProjects, setSavedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [mainMode, setMainMode] = useState('buttons');
  const [imageAnalysisResult, setImageAnalysisResult] = useState(null);
  const [imageAnalysisDraft, setImageAnalysisDraft] = useState(null);
  const [massingResult, setMassingResult] = useState(null);

  // NEW: controls PaneRight
  const [rightMode, setRightMode] = useState(null); // 'message' | 'network' | 'login' | null

  // Full-screen 3D. Lives here rather than in Site3DView because the panes it
  // collapses are siblings of the viewport, not children of it — the 3D view can
  // ask, but only the grid that owns all three can answer.
  const [focusMode, setFocusMode] = useState(false);
  useEffect(() => {
    const onToggle = (e) => setFocusMode((v) => (e.detail?.on != null ? !!e.detail.on : !v));
    // Escape is the expected way out of anything full-screen, and without it a
    // user whose pointer never finds the button again is stuck.
    const onKey = (e) => { if (e.key === 'Escape') setFocusMode(false); };
    window.addEventListener('toggleFocusMode', onToggle);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('toggleFocusMode', onToggle);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  const onMassing = (e) => { setMassingResult(e.detail); setRightMode('imageUpload'); };

  useEffect(() => {
    if (imageAnalysisDraft) {
      console.log('Use Detected Polygon fired:', imageAnalysisDraft);
    }
  }, [imageAnalysisDraft]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/projects`
        );
        // 401 is the normal state before signing in, not a failure to report.
        // The list is emptied rather than left alone, so signing out does not
        // leave the previous account's projects on screen.
        if (response.status === 401) {
          // Back to the landing view as well — leaving mainMode alone keeps the
          // 3D scene mounted with the previous account's site still in it.
          setSavedProjects([]); setSelectedProject(null); setMainMode('buttons');
          // The server has rejected whatever token we hold, so stop claiming to
          // be signed in. Expired sessions surface here rather than in the
          // account panel, which is usually not even mounted.
          clearSession();
          return;
        }
        if (!response.ok) throw new Error('Failed to load projects');
        const data = await response.json();

        // Ensure each project has an id field
        const normalized = data.map((p, idx) => ({
          ...p,
          id: typeof p.id === 'number' ? p.id : idx,
        }));

        setSavedProjects(normalized);
      } catch (err) {
        console.error('Error loading projects from server', err);
      }
    };

    fetchProjects();
    // Projects belong to an account, so the list has to follow sign-in and
    // sign-out — see writeToken in AccountPanel.
    window.addEventListener('decon:auth', fetchProjects);
    return () => window.removeEventListener('decon:auth', fetchProjects);
  }, []);

  // "Message" on a Network card: switch the pane AND say who to open. Two bits
  // of state because the pane is App's and the person is the panel's — passing
  // the id as a prop is what lets Network hand off without Message having to
  // know Network exists.
  const [messageWith, setMessageWith] = useState(null);
  useEffect(() => {
    const onMessageWith = (e) => {
      const id = e.detail?.userId;
      if (!id) return;
      setMessageWith(id);
      setRightMode('message');
    };
    window.addEventListener('decon:message-with', onMessageWith);
    return () => window.removeEventListener('decon:message-with', onMessageWith);
  }, []);

  useEffect(() => {
    const handleOpenRightPanel = (e) => {
      const panel = e.detail?.panel || 'imageUpload';
      // Map panel names to your rightMode choices
      // For now we can just use 'message' or define a new 'imageUpload' mode
      setRightMode(panel); // or setRightMode('message') if you prefer
    };

    const handleCloseRightPanel = () => {
      setRightMode(null);
    };

    window.addEventListener('openRightPanel', handleOpenRightPanel);
    window.addEventListener('closeRightPanel', handleCloseRightPanel);

    return () => {
      window.removeEventListener('openRightPanel', handleOpenRightPanel);
      window.removeEventListener('closeRightPanel', handleCloseRightPanel);
    };
  }, []);

  useEffect(() => {
    const handleImageAnalysisComplete = (e) => {
      setImageAnalysisResult(e.detail || null);
    };

    window.addEventListener('imageAnalysisComplete', handleImageAnalysisComplete);

    return () => {
      window.removeEventListener('imageAnalysisComplete', handleImageAnalysisComplete);
    };
  }, []);

  useEffect(() => {
    const handleUseDetectedImagePolygon = (event) => {
      const result = event.detail;
      if (!result) return;
      setImageAnalysisDraft(result);
    };

    window.addEventListener(
      'useDetectedImagePolygon',
      handleUseDetectedImagePolygon
    );

    return () => {
      window.removeEventListener(
        'useDetectedImagePolygon',
        handleUseDetectedImagePolygon
      );
    };
  }, []);

  useEffect(() => {
    const onMassing = (e) => { setMassingResult(e.detail); setRightMode('costAnalysis'); };
    window.addEventListener('massingBuilt', onMassing);
    return () => window.removeEventListener('massingBuilt', onMassing);
  }, []);

  useEffect(() => {
    const onCleared = () => setMassingResult(null);
    window.addEventListener('clearMassingRequested', onCleared);
    return () => window.removeEventListener('clearMassingRequested', onCleared);
  }, []);

  // On a narrow window the right pane is a drawer over the view (main.css,
  // "Responsive shell"): Escape closes it, as it would any drawer. On a wide
  // screen it is a column, and Escape is left to whatever else uses it.
  useEffect(() => {
    if (!rightMode) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !window.matchMedia('(max-width: 1080px)').matches) return;
      setRightMode(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rightMode]);

  // helper: toggle a mode, clicking again closes it
  const handleRightMode = (mode) => {
    setRightMode((current) => (current === mode ? null : mode));
  };

  console.log('PaneMain imageAnalysisResult:', imageAnalysisResult);

  return (
    <div className={`app ${rightMode ? 'app--with-right' : 'app--no-right'}${focusMode ? ' app--focus' : ''}`} 
      id="app">
      <Header
        rightMode={rightMode}
        onChangeRightMode={handleRightMode}
      />

      <PaneLeft
        savedProjects={savedProjects}
        setSavedProjects={setSavedProjects}
        setSelectedProject={setSelectedProject}
        setMainMode={setMainMode}
      />

      <div
        className="resizer-vertical"
        id="resizer-left"
        style={{ gridArea: 'left' }}
      />

      <PaneMain
        savedProjects={savedProjects}
        setSavedProjects={setSavedProjects}
        selectedProject={selectedProject}
        mainMode={mainMode}
        setMainMode={setMainMode}
        imageAnalysisResult={imageAnalysisResult || imageAnalysisDraft}
      />

      {rightMode && (
        <>
          <div
            className="resizer-vertical"
            id="resizer-right"
            style={{ gridArea: 'right' }}
          />
          {/* Below 1080px only (display:none wider): the drawer's backdrop, which
              closes it on a click, and a close button. Escape closes it too. */}
          <div className="pane-right-scrim" aria-hidden="true" onClick={() => setRightMode(null)} />
          <PaneRight mode={rightMode} selectedProject={selectedProject} massingResult={massingResult} messageWith={messageWith} />
          <button
            type="button"
            className="pane-right-close"
            aria-label="Close panel"
            title="Close (Esc)"
            onClick={() => setRightMode(null)}
          >
            ×
          </button>
        </>
      )}

      <Footer />
    </div>
  );
}

export default App;
